import { useState } from "react";
import { CalendarClock, PackageCheck, Store, TrendingUp, UserCog, Wallet } from "lucide-react";
import { brl, dataBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Surface } from "@/components/ds";
import { ProfileModal } from "@/components/partner/ProfileModal";

const Stat = ({ label, value, hint, testid, icon: Icon }) => (
  <Surface className="min-w-0 overflow-hidden p-5" data-testid={testid}>
    <div className="flex items-start justify-between gap-2">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      {Icon && <Icon className="h-4 w-4 shrink-0 text-brand" />}
    </div>
    <p className="mt-2 font-num text-2xl">{value}</p>
    {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
  </Surface>
);

export function PartnerHome({ me, onReload, onGoTo }) {
  const [editing, setEditing] = useState(false);

  return (
    <div className="space-y-6" data-testid="partner-home">
      <div className="grid grid-cols-2 gap-4 stagger lg:grid-cols-3">
        <Stat
          label="Meu estoque"
          value={`${Number(me.consigned_units).toLocaleString("pt-BR")} un`}
          hint="peças que estão comigo"
          icon={PackageCheck}
          testid="partner-stat-consigned"
        />
        <Stat
          label="Valor em estoque"
          value={brl(me.stock_value)}
          hint={
            me.stock_unpriced_units > 0
              ? `${Number(me.stock_unpriced_units).toLocaleString("pt-BR")} un sem preço definido`
              : "pelo preço de consignação"
          }
          icon={Wallet}
          testid="partner-stat-stock-value"
        />
        <Stat
          label="Potencial de vendas"
          value={brl(me.sales_potential)}
          hint={
            me.potential_unpriced_units > 0
              ? `${Number(me.potential_unpriced_units).toLocaleString("pt-BR")} un sem preço de catálogo`
              : "pelo preço final do catálogo"
          }
          icon={Store}
          testid="partner-stat-sales-potential"
        />
        <Stat
          label="Margem potencial"
          value={brl(me.potential_margin)}
          hint={
            me.potential_partial
              ? "parcial: há itens sem preço"
              : "diferença entre catálogo e consignação"
          }
          icon={TrendingUp}
          testid="partner-stat-potential-margin"
        />
        <Stat
          label="Reposições"
          value={me.pending_replenishments}
          hint="pedidos em andamento"
          testid="partner-stat-pending-reps"
        />
        <Stat
          label="Devoluções"
          value={me.pending_returns}
          hint="solicitações em andamento"
          testid="partner-stat-pending-rets"
        />
        <Stat
          label={`Fechamento ${me.current_period_label}`}
          value={brl(me.current_amount)}
          hint={`${Number(me.current_sold_units).toLocaleString("pt-BR")} un vendidas no período`}
          testid="partner-stat-current-closing"
        />
        <Stat
          label="Próximo fechamento"
          value={dataBR(me.next_closing_at)}
          hint={`em ${me.days_to_closing} dia(s)`}
          icon={CalendarClock}
          testid="partner-stat-next-closing"
        />
      </div>

      {me.open_amount > 0 && (
        <Surface className="p-5" data-testid="partner-open-amount">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Valor em aberto</p>
          <p className="mt-1 font-num text-xl text-brand">{brl(me.open_amount)}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {me.open_closures} fechamento(s) aguardando pagamento.
          </p>
          <Button
            variant="outline"
            onClick={() => onGoTo("fechamentos")}
            data-testid="go-to-closures-btn"
            className="mt-3 h-10 rounded-full px-5 text-xs"
          >
            Ver fechamentos
          </Button>
        </Surface>
      )}

      <Surface className="flex flex-wrap items-center justify-between gap-3 p-5">
        <div className="min-w-0">
          <p className="text-sm font-medium">{me.name}</p>
          <p className="truncate text-xs text-muted-foreground">
            {me.email}
            {me.contact ? ` · ${me.contact}` : ""}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => setEditing(true)}
          data-testid="edit-profile-btn"
          className="h-10 gap-2 rounded-full px-5 text-xs"
        >
          <UserCog className="h-4 w-4" /> Meu perfil
        </Button>
      </Surface>

      {editing && (
        <ProfileModal me={me} onClose={() => setEditing(false)} onSaved={onReload} />
      )}
    </div>
  );
}
